import { AfkPingTracker, type ConnectionManager } from '@bn-games/room-client';
import { playAudioSample } from '@bn-games/audio-core';
import {
  colorForSeat,
  deserializeMove,
  parseVariant,
  resolveSeatColor,
  serializeMove,
  type ClientMessage,
  type GameSnapshot,
  type Player,
  type ServerMessage,
  type StateSyncMessage,
} from './protocol.js';
import type { Color, Move } from '../game/types.js';
import type { Variant } from '../settings/types.js';

const RECONNECT_GRACE_MS = 300_000;

/**
 * Canvas-overlay states. The TurnBanner remains the source of truth for
 * whose move it is; this overlay layers on top during connection issues
 * and rematch handshakes.
 */
export type OnlineOverlay =
  | 'none'
  | 'opponent_disconnected'
  | 'reconnecting_self'
  | 'game_disconnected'
  | 'rematch_offered'
  | 'rematch_requested'
  | 'rematch_declined'
  | 'game_over';

export type GameOverResult = 'win' | 'loss' | 'draw';

export type OverlayChangeListener = () => void;

/**
 * Optional callbacks the in-game overlay subscribes to so it can react to
 * server-driven resign / restart-consent transitions.
 */
export interface OverlayWiringHandlers {
  readonly onResign?: (winnerIsLocal: boolean) => void;
  readonly onRestartStart?: () => void;
  readonly onRestartStateChange?: () => void;
}

export interface NetworkGameControllerOptions {
  readonly connection: ConnectionManager<ClientMessage, ServerMessage>;
  readonly localSeat: number;
  readonly onRemoteMove: (move: Move) => void;
  readonly onOverlayChange: OverlayChangeListener;
  readonly onRematchStart: (newColor: Color, variant: Variant) => void;
  readonly getSnapshot: () => GameSnapshot;
  readonly applySnapshot: (snapshot: GameSnapshot) => void;
}

/**
 * Bridges the room's gameplay protocol to the local game loop. Local moves
 * are committed by the player (via `MoveSelector`); we forward them to the
 * server. Remote moves are pushed into the local state via the `onRemoteMove`
 * callback, which kicks off the same animation pipeline that local moves
 * trigger.
 *
 * No per-step chain relay — a Checkers `Move` already encodes the full chain
 * (path + captures), so a single CHECKERS_MOVE message represents one turn.
 */
export class NetworkGameController {
  private readonly opts: NetworkGameControllerOptions;
  private readonly localPlayer: Player;
  private unsubMessage: (() => void) | null = null;
  private unsubState: (() => void) | null = null;
  private _overlay: OnlineOverlay = 'none';
  private _opponentName = 'Opponent';
  private _opponentVerified = false;
  private _opponentAvatarUrl: string | null = null;
  private _opponentUserId: string | null = null;
  private _gracePeriodEndsAt: number | null = null;
  private _gameOverResult: GameOverResult | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  // ─── Resign / restart-consent ──────────────────────────────────
  private _restartPending = false;
  private _localRestartUsed = false;
  private overlayHandlers: OverlayWiringHandlers = {};
  // ─── AFK ping ──────────────────────────────────────────────────
  private readonly afkPingTracker = new AfkPingTracker();
  // Dedupes notifyTurnState — main.ts calls on every state mutation but the
  // tracker only cares about turn flips.
  private lastTrackerTurnActive: boolean | null = null;
  private settingsGetter: (() => { afkRemindersEnabled: boolean }) | null = null;

  constructor(opts: NetworkGameControllerOptions) {
    this.opts = opts;
    this.localPlayer = colorForSeat(opts.localSeat);
  }

  get overlay(): OnlineOverlay {
    return this._overlay;
  }

  get opponentName(): string {
    return this._opponentName;
  }

  get opponentVerified(): boolean {
    return this._opponentVerified;
  }

  get opponentAvatarUrl(): string | null {
    return this._opponentAvatarUrl;
  }

  get opponentUserId(): string | null {
    return this._opponentUserId;
  }

  get gracePeriodEndsAt(): number | null {
    return this._gracePeriodEndsAt;
  }

  get gameOverResult(): GameOverResult | null {
    return this._gameOverResult;
  }

  /** The color the local player is playing this game (derived from seat). */
  getLocalPlayer(): Player {
    return this.localPlayer;
  }

  setOpponent(name: string, verified: boolean, avatarUrl: string | null, userId: string | null): void {
    this._opponentName = name;
    this._opponentVerified = verified;
    this._opponentAvatarUrl = avatarUrl;
    this._opponentUserId = userId;
  }

  showGameOver(result: GameOverResult): void {
    if (this._overlay === 'game_disconnected') return; // disconnect takes priority
    this._gameOverResult = result;
    this.setOverlay('game_over');
  }

  clearGameOver(): void {
    this._gameOverResult = null;
    if (this._overlay === 'game_over' || this._overlay === 'rematch_declined') {
      this.setOverlay('none');
    }
  }

  attach(): void {
    this.unsubMessage = this.opts.connection.onMessage((msg) => this.handleServerMessage(msg));
    this.unsubState = this.opts.connection.onStateChange((state) => {
      if (state === 'disconnected' && this._overlay !== 'game_disconnected') {
        // Local-end estimate; the server's authoritative value reaches the
        // survivor via OPPONENT_DISCONNECTED. We just sketch the countdown.
        this._gracePeriodEndsAt = Date.now() + RECONNECT_GRACE_MS;
        this.setOverlay('reconnecting_self');
      } else if (state === 'connected' && this._overlay === 'reconnecting_self') {
        // Stop the grace countdown — RECONNECT_OK / RECONNECT_FAIL decides
        // the final overlay state. Letting the timer keep running would
        // falsely fire "Connection lost" if RECONNECT_OK is delayed.
        this._gracePeriodEndsAt = null;
        this.updateCountdownTicker();
      }
    });
  }

  detach(): void {
    if (this.unsubMessage) {
      this.unsubMessage();
      this.unsubMessage = null;
    }
    if (this.unsubState) {
      this.unsubState();
      this.unsubState = null;
    }
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.afkPingTracker.dispose();
  }

  // ─── AFK ping ─────────────────────────────────────────────────────

  getAfkPingTracker(): AfkPingTracker {
    return this.afkPingTracker;
  }

  setSettingsGetter(getter: () => { afkRemindersEnabled: boolean }): void {
    this.settingsGetter = getter;
  }

  sendAfkPing(): void {
    this.opts.connection.send({ type: 'PING_OPPONENT' });
    this.afkPingTracker.markPingSent();
  }

  /**
   * Called by main.ts whenever the local game's turn ownership may have
   * changed (after applyMove, snapshot adoption, new game start). The
   * dedupe guard means callers don't need to debounce.
   */
  notifyTurnState(isOpponentTurn: boolean): void {
    if (this.lastTrackerTurnActive === isOpponentTurn) return;
    this.lastTrackerTurnActive = isOpponentTurn;
    this.afkPingTracker.setOpponentTurn(isOpponentTurn);
  }

  markGameDisconnected(): void {
    this._gracePeriodEndsAt = null;
    this.setOverlay('game_disconnected');
  }

  notifyLocalMove(move: Move): void {
    this.opts.connection.send({ type: 'CHECKERS_MOVE', move: serializeMove(move) });
  }

  notifyGameOver(result: 'win' | 'draw', winner?: Color): void {
    if (winner !== undefined) {
      this.opts.connection.send({ type: 'CHECKERS_GAME_OVER', result, winner });
    } else {
      this.opts.connection.send({ type: 'CHECKERS_GAME_OVER', result });
    }
  }

  requestRematch(): void {
    this.opts.connection.send({ type: 'REMATCH_REQUEST' });
    this.setOverlay('rematch_offered');
  }

  acceptRematch(): void {
    this.opts.connection.send({ type: 'REMATCH_ACCEPT' });
    this.setOverlay('none');
  }

  declineRematch(): void {
    this.opts.connection.send({ type: 'REMATCH_DECLINE' });
    this.setOverlay('none');
  }

  dismissOverlay(): void {
    this.setOverlay('none');
  }

  // ─── Resign / restart-consent (in-game overlay) ────────────────────

  setOverlayHandlers(handlers: OverlayWiringHandlers): void {
    this.overlayHandlers = handlers;
  }

  isRestartPending(): boolean {
    return this._restartPending;
  }

  canLocalProposeRestart(): boolean {
    return !this._localRestartUsed;
  }

  requestResign(): void {
    this.opts.connection.send({ type: 'RESIGN' });
  }

  requestRestart(): void {
    if (this._restartPending || this._localRestartUsed) return;
    this._localRestartUsed = true;
    this.opts.connection.send({ type: 'RESTART_REQUEST' });
    this.overlayHandlers.onRestartStateChange?.();
  }

  acceptRestart(): void {
    this.opts.connection.send({ type: 'RESTART_ACCEPT' });
  }

  declineRestart(): void {
    this.opts.connection.send({ type: 'RESTART_DECLINE' });
  }

  private setOverlay(overlay: OnlineOverlay): void {
    if (this._overlay === overlay) return;
    this._overlay = overlay;
    this.updateCountdownTicker();
    this.opts.onOverlayChange();
  }

  private updateCountdownTicker(): void {
    const wantsTick =
      this._gracePeriodEndsAt !== null &&
      (this._overlay === 'opponent_disconnected' || this._overlay === 'reconnecting_self');
    if (wantsTick && this.countdownTimer === null) {
      this.countdownTimer = setInterval(() => {
        if (this._gracePeriodEndsAt !== null && Date.now() >= this._gracePeriodEndsAt) {
          this.markGameDisconnected();
          return;
        }
        this.opts.onOverlayChange();
      }, 1000);
    } else if (!wantsTick && this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private handleServerMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'CHECKERS_MOVE':
        this.opts.onRemoteMove(deserializeMove(msg.move));
        break;

      case 'OPPONENT_DISCONNECTED':
        this._gracePeriodEndsAt = msg.gracePeriodEndsAt;
        this.setOverlay('opponent_disconnected');
        break;

      case 'OPPONENT_RECONNECTED':
        if (this._overlay === 'opponent_disconnected') {
          this._gracePeriodEndsAt = null;
          this.setOverlay('none');
        }
        break;

      case 'REQUEST_STATE': {
        const snapshot: StateSyncMessage = { type: 'STATE_SYNC', snapshot: this.opts.getSnapshot() };
        this.opts.connection.send(snapshot);
        break;
      }

      case 'STATE_SYNC':
        this.opts.applySnapshot(msg.snapshot);
        break;

      case 'RECONNECT_OK':
        this._gracePeriodEndsAt = null;
        if (this._overlay === 'reconnecting_self') this.setOverlay('none');
        break;

      case 'RECONNECT_FAIL':
        this.markGameDisconnected();
        break;

      case 'REMATCH_REQUEST':
        this.setOverlay('rematch_requested');
        break;

      case 'REMATCH_DECLINE':
        this.setOverlay('rematch_declined');
        break;

      case 'REMATCH_START':
        this.setOverlay('none');
        this._restartPending = false;
        this._localRestartUsed = false;
        this.opts.onRematchStart(resolveSeatColor(msg.seats, msg.seat), parseVariant(msg.variant));
        break;

      case 'RESIGN': {
        // Server is authoritative — trust msg.winner. For N=2 (always for
        // checkers) this matches "the opponent won iff we didn't resign",
        // but the seat-comparison form is what the protocol contract reads.
        const winnerIsLocal = msg.winner === this.opts.localSeat;
        this.overlayHandlers.onResign?.(winnerIsLocal);
        break;
      }

      case 'RESTART_PENDING':
        this._restartPending = true;
        this.overlayHandlers.onRestartStateChange?.();
        break;

      case 'RESTART_CANCELLED':
        this._restartPending = false;
        this.overlayHandlers.onRestartStateChange?.();
        break;

      case 'RESTART_REJECTED':
        this._localRestartUsed = false;
        this.overlayHandlers.onRestartStateChange?.();
        break;

      case 'RESTART_START':
        this._restartPending = false;
        this._localRestartUsed = false;
        this.overlayHandlers.onRestartStart?.();
        this.overlayHandlers.onRestartStateChange?.();
        break;

      case 'OPPONENT_PINGED': {
        if (this.settingsGetter?.().afkRemindersEnabled) {
          playAudioSample(`${import.meta.env.BASE_URL}sounds/ping.m4a`);
        }
        break;
      }
    }
  }
}
